import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * StageTracker — V4-4-9 / ADR-0029 · Contract C16.
 *
 * Horizontal (>= 768 px) / vertical (mobile) ordered stages with
 * lane-coloured rail and semantic status tokens. Compact variant
 * renders a current-stage chip for lists.
 *
 * C16: StageTracker may not receive free-text status strings — status
 * must be the StageStatus enum.
 */
import React from 'react';
// Accessibility Blocker fix (2026-09-22): --success (#10B981) and --warning
// (#F59E0B) are decorative-mark tokens that fail WCAG 1.4.3 as text on
// --surface-elevated (2.54:1 and 2.15:1). Use --success-fg/--warning-text/
// --danger-fg which resolve to the contrast-safe band base colors (>=4.5:1).
const STATUS_TOKENS = {
    not_started: { color: 'var(--text-dim)', icon: '○', label: 'Not started' },
    in_progress: { color: 'var(--accent)', icon: '◔', label: 'In progress' },
    blocked: { color: 'var(--danger-fg)', icon: '✖', label: 'Blocked' },
    done: { color: 'var(--success-fg)', icon: '✔', label: 'Done' },
    not_applicable: { color: 'var(--text-dim)', icon: '—', label: 'N/A' },
    attention: { color: 'var(--warning-text)', icon: '⚠', label: 'Attention' },
};
const LANE_TOKENS = {
    buying: 'var(--buying, var(--accent))',
    building: 'var(--building, var(--accent))',
};
export function StageTracker({ lane, stages, current, compact = false, className, presentation = 'steps', }) {
    if (presentation === 'lifecycle') {
        return _jsx("nav", { "aria-label": "System lifecycle", className: className, style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 8rem), 1fr))', gap: 'var(--space-2)' }, children: stages.map(stage => _jsxs("a", { href: stage.href, style: { display: 'grid', gap: 'var(--space-2)', minHeight: 88, padding: 'var(--space-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface-elevated)', color: 'var(--text)', textDecoration: 'none' }, children: [_jsx("strong", { children: stage.label }), _jsx("span", { style: { color: 'var(--text)', fontSize: 'var(--font-size-sm)' }, children: stage.detail ?? STATUS_TOKENS[stage.status].label })] }, stage.key)) });
    }
    if (compact) {
        const currentStage = stages.find((s) => s.key === current);
        if (!currentStage) {
            return _jsx("span", { "data-stage-tracker-chip": true, className: className });
        }
        const token = STATUS_TOKENS[currentStage.status];
        return (_jsxs("span", { "data-stage-tracker-chip": true, className: className, role: "status", "aria-label": `${currentStage.label}: ${token.label}`, style: {
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1, 4px)',
                padding: '2px var(--space-2, 8px)',
                borderRadius: 'var(--radius-chip, 12px)',
                fontSize: 'var(--font-size-3xs, 11px)',
                fontFamily: 'var(--font-ui)',
                color: token.color,
                border: `1px solid ${token.color}`,
                backgroundColor: 'transparent',
            }, children: [_jsx("span", { "aria-hidden": "true", children: token.icon }), currentStage.label] }));
    }
    const laneColor = LANE_TOKENS[lane];
    return (_jsx("nav", { "data-stage-tracker": true, className: className, "aria-label": `${lane === 'buying' ? 'Buy' : 'Build'} stage tracker`, role: "list", style: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 'var(--space-1, 4px)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-xs, 12px)',
        }, children: stages.map((stage, idx) => {
            const token = STATUS_TOKENS[stage.status];
            const isCurrent = stage.key === current;
            const isNa = stage.status === 'not_applicable';
            const stageStyle = {
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1, 4px)',
                padding: '4px var(--space-2, 8px)',
                borderRadius: 'var(--radius-chip, 12px)',
                color: token.color,
                textDecoration: isNa ? 'line-through' : 'none',
                fontWeight: isCurrent ? 600 : 400,
                backgroundColor: isCurrent ? `color-mix(in srgb, ${laneColor} 12%, transparent)` : 'transparent',
            };
            const stageTitle = isNa && stage.detail ? `N/A: ${stage.detail}` : stage.detail ?? undefined;
            const stageContent = _jsxs(_Fragment, { children: [_jsx("span", { "aria-hidden": "true", style: { fontSize: '0.9em' }, children: token.icon }), _jsx("span", { children: stage.label })] });
            // Accessibility Blocker fix: render <a> for navigable stages so
            // keyboard users can Tab to and Enter/activate each stage.
            const stageEl = stage.href ? (_jsx("a", { href: stage.href, role: "listitem", "aria-current": isCurrent ? 'step' : undefined, style: stageStyle, title: stageTitle, children: stageContent }, stage.key)) : (_jsx("div", { role: "listitem", "aria-current": isCurrent ? 'step' : undefined, style: stageStyle, title: stageTitle, children: stageContent }, stage.key));
            return (_jsxs(React.Fragment, { children: [stageEl, idx < stages.length - 1 && (_jsx("span", { "aria-hidden": "true", style: {
                            display: 'flex',
                            alignItems: 'center',
                            color: laneColor,
                            fontSize: '0.7em',
                        }, children: '▶' }))] }, stage.key));
        }) }));
}
//# sourceMappingURL=StageTracker.js.map